/**
 * useCalls Hook
 * 
 * Custom hook for fetching and managing calls list.
 * Handles loading states, errors, and data fetching.
 * 
 * Benefits:
 * - Encapsulates all calls list logic
 * - Reusable across components
 * - Handles loading and error states
 * - Provides refetch functionality
 * - Manages pagination
 * 
 * @example
 * const { calls, isLoading, error, fetchCalls, refetch } = useCalls();
 * 
 * useEffect(() => {
 *   fetchCalls({ filter_criteria: { call_status: ['ended'] } });
 * }, []);
 */

import { useState, useCallback } from 'react';
import { listCalls } from '../api/callsApi';

/**
 * Custom hook for managing calls list
 * 
 * @returns {object} Hook state and methods
 * @returns {Array} calls - Array of call objects
 * @returns {boolean} isLoading - True while fetching data
 * @returns {object|null} error - Error object if request failed
 * @returns {Function} fetchCalls - Function to fetch calls with params
 * @returns {Function} refetch - Function to refetch with last params
 * @returns {Function} reset - Function to reset state
 * @returns {string|null} paginationKey - Key for next page (last call_id)
 * @returns {boolean} hasMore - True if more calls available
 */
export const useCalls = () => {
  // State management
  const [calls, setCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastParams, setLastParams] = useState(null);
  const [paginationKey, setPaginationKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  /**
   * Fetch calls with given parameters
   * 
   * @param {object} params - Query parameters for listCalls API
   * @param {string} params.restaurant_name - Optional restaurant name filter
   * @param {object} params.filter_criteria - Filter criteria
   * @param {string} params.sort_order - Sort order
   * @param {number} params.limit - Limit
   * @param {string} params.pagination_key - Pagination key
   * @param {boolean} append - If true, appends to existing calls (for pagination)
   */
  const fetchCalls = useCallback(async (params = {}, append = false) => {
    // Set loading state
    setIsLoading(true);
    setError(null);

    try {
      // Call API with all params (including restaurant_name if provided)
      const response = await listCalls(params);

      // Validate response is an array
      if (!Array.isArray(response)) {
        const error = new Error('Invalid response format');
        error.code = 'INVALID_RESPONSE';
        throw error;
      }

      // Update calls state
      if (append) {
        // Append to existing calls (pagination)
        setCalls(prevCalls => [...prevCalls, ...response]);
      } else {
        // Replace calls (new search/filter)
        setCalls(response);
      }

      // Update pagination state
      if (response.length > 0) {
        // Last call's ID is the pagination key for next page
        const lastCall = response[response.length - 1];
        setPaginationKey(lastCall.call_id);
        
        // If we got less than the limit, there are no more calls
        const limit = params.limit || 1000; // Use max API limit as default
        setHasMore(response.length >= limit);
      } else {
        // No calls returned
        setPaginationKey(null);
        setHasMore(false);
      }

      // Store params for refetch
      setLastParams(params);

      // Clear loading state
      setIsLoading(false);
    } catch (err) {
      // Handle error
      console.error('Error fetching calls:', err);
      
      setError({
        message: err.message || 'Failed to fetch calls',
        code: err.code || 'FETCH_ERROR',
      });
      
      setIsLoading(false);
      
      // Don't clear calls on error - keep showing previous data
    }
  }, []);

  /**
   * Refetch calls with last used parameters
   * Useful for "refresh" button
   */
  const refetch = useCallback(() => {
    if (lastParams) {
      fetchCalls(lastParams, false);
    }
  }, [lastParams, fetchCalls]);

  /**
   * Load next page of calls
   * Uses pagination_key from last fetch
   */
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore && paginationKey && lastParams) {
      // Fetch next page with pagination key
      fetchCalls(
        {
          ...lastParams,
          pagination_key: paginationKey,
        },
        true // Append to existing calls
      );
    }
  }, [isLoading, hasMore, paginationKey, lastParams, fetchCalls]);

  /**
   * Reset hook state
   * Clears calls, errors, and pagination
   */
  const reset = useCallback(() => {
    setCalls([]);
    setIsLoading(false);
    setError(null);
    setLastParams(null);
    setPaginationKey(null);
    setHasMore(true);
  }, []);

  return {
    // State
    calls,
    isLoading,
    error,
    paginationKey,
    hasMore,
    
    // Methods
    fetchCalls,
    refetch,
    loadMore,
    reset,
  };
};

export default useCalls;

